/**
 * `/analytics/funnel` — Attribution funnel: posts → clicks → leads → bookings (F1).
 */
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useFunnel } from "@/hooks/use-content";
import { formatTimestamp } from "@/lib/formatters";
import { Link } from "react-router-dom";

export function AnalyticsFunnelPage() {
  const funnel = useFunnel();

  const summary = funnel.data?.pipeline_summary ?? {};
  const summaryEntries = Object.entries(summary).filter(([, v]) => v > 0);

  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Analytics</p>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Attribution Funnel</h1>
        <p className="text-sm text-muted-foreground">
          How your content drives leads and bookings — last 30 days.
        </p>
      </header>

      {funnel.isLoading && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      )}

      {funnel.isError && (
        <p className="text-sm text-destructive">Failed to load funnel data.</p>
      )}

      {funnel.data && (
        <>
          {/* Pipeline summary */}
          {summaryEntries.length > 0 && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {summaryEntries.map(([status, count]) => (
                <Card key={status} className="border-0 bg-gray-50 shadow-none">
                  <CardContent className="p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground capitalize">
                      {status}
                    </p>
                    <p className="mt-1 text-2xl font-bold text-gray-900">{count}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Per-post funnel */}
          {funnel.data.by_post.length === 0 ? (
            <div className="flex flex-col items-center gap-4 rounded-xl border-2 border-dashed border-gray-200 p-12 text-center">
              <p className="text-sm text-muted-foreground">No published posts with attribution yet.</p>
              <Link to="/content" className="text-sm text-primary hover:underline">
                Go to Content →
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {/* Header row */}
              <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-6 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <span>Post</span>
                <span className="text-right">Clicks</span>
                <span className="text-right">Leads</span>
                <span className="text-right">Booked</span>
                <span className="text-right">Closed</span>
              </div>

              <Card className="border-gray-200 overflow-hidden">
                {funnel.data.by_post.map((item, idx) => (
                  <Link
                    key={item.post_id}
                    to={`/content/posts/${item.post_id}`}
                    className={`grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-6 px-4 py-3 hover:bg-gray-50 transition-colors ${
                      idx > 0 ? "border-t border-gray-100" : ""
                    }`}
                  >
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="text-xs font-semibold uppercase text-gray-400">
                        {item.platform}
                        {item.published_at && (
                          <> · {formatTimestamp(item.published_at)}</>
                        )}
                      </span>
                      <span className="text-sm text-gray-900 truncate">{item.body_preview}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900 tabular-nums self-center">
                      {item.clicks}
                    </span>
                    <span className="text-sm text-gray-700 tabular-nums self-center">
                      {item.leads}
                    </span>
                    <span className="text-sm text-green-700 tabular-nums self-center font-medium">
                      {item.booked}
                    </span>
                    <span className="text-sm text-emerald-700 tabular-nums self-center font-semibold">
                      {item.closed}
                    </span>
                  </Link>
                ))}
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AnalyticsFunnelPage;
